import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/api/axiosInstance'

const QUOTES_PATH = '/service-quote-requests/'

const quoteKeys = {
    list: ['service-quote-requests'] as const,
    item: (id: number) => ['service-quote-requests', id] as const,
}

export interface CreateQuotePayload {
    offering: number
    description: string
    district?: number | null
    attachment_urls?: string[]
}

export interface QuoteActionPayload {
    id: number
    payload?: any
}

export const useQuoteRequests = (enabled: boolean = true) => {
    return useQuery({
        queryKey: quoteKeys.list,
        queryFn: async () => {
            const response = await axiosInstance.get(QUOTES_PATH)
            return response.data?.results ?? response.data
        },
        enabled,
    })
}

export const useQuoteRequest = (id?: number) => {
    return useQuery({
        queryKey: quoteKeys.item(id ?? 0),
        queryFn: async () => {
            if (!id) return null
            const response = await axiosInstance.get(`${QUOTES_PATH}${id}/`)
            return response.data
        },
        enabled: !!id,
    })
}

export const useCreateQuoteRequest = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data: CreateQuotePayload) => {
            const response = await axiosInstance.post(QUOTES_PATH, data)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: quoteKeys.list })
        },
    })
}

const useQuoteActionMutation = (action: string) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, payload }: QuoteActionPayload) => {
            const response = await axiosInstance.post(`${QUOTES_PATH}${id}/${action}/`, payload ?? {})
            return response.data
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: quoteKeys.list })
            queryClient.invalidateQueries({ queryKey: quoteKeys.item(variables.id) })
        },
    })
}

export const useRequestQuoteVisit = () => useQuoteActionMutation('request_visit')
export const useApproveQuoteVisit = () => useQuoteActionMutation('approve_visit')
export const useRejectQuoteVisit = () => useQuoteActionMutation('reject_visit')
export const useCompleteQuoteVisit = () => useQuoteActionMutation('complete_visit')
export const useSendQuoteResponse = () => useQuoteActionMutation('send_quote')
export const useAcceptQuote = () => useQuoteActionMutation('accept_quote')
export const useDeclineQuote = () => useQuoteActionMutation('decline_quote')


